import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sound } from "@/lib/audio";

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.22, delayChildren: 0.3 } },
};
const lineUp = {
    hidden: { y: "115%" },
    show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Intro({ onDone, reduce }) {
    const [open, setOpen] = useState(false);

    const reveal = () => {
        sound.whoosh();
        setOpen(true);
    };

    const exit = (e) => {
        if (e) e.stopPropagation();
        sound.chime(1046.5);
        onDone();
    };

    const d = reduce ? 0 : 1;

    return (
        <motion.div
            data-testid="intro-overlay"
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-[#0B0716] px-6"
            exit={{ opacity: 0, scale: 1.06, transition: { duration: 0.8, ease: "easeInOut" } }}
        >
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute left-1/2 top-1/2 h-[80vmin] w-[80vmin] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#6B3882] opacity-20 blur-[120px]" />
            </div>

            <button
                data-testid="skip-intro-button"
                onClick={exit}
                className="eyebrow-label absolute right-5 top-5 z-10 rounded-full border border-[#F3D085]/25 px-4 py-2 transition-colors hover:border-[#F3D085]/70 hover:text-[#F3D085]"
            >
                Skip intro →
            </button>

            <AnimatePresence mode="wait">
                {!open ? (
                    <motion.div
                        key="sealed"
                        className="relative z-[1] flex flex-col items-center text-center"
                        variants={container}
                        initial="hidden"
                        animate="show"
                        exit={{ opacity: 0, y: -30, transition: { duration: 0.4 } }}
                    >
                        <span className="mask-line mb-6">
                            <motion.span variants={lineUp} custom={d} className="eyebrow-label">
                                CONFIDENTIAL: FOR MARY MANZUR'S EYES ONLY
                            </motion.span>
                        </span>

                        <div className="envelope-scene mb-10 mt-2">
                            <div className="envelope" data-open="false">
                                <div className="env-back" />
                                <div className="env-letter-card">
                                    <span className="font-display text-3xl italic text-[#7a5a10]">M.</span>
                                </div>
                                <div className="env-front" />
                                <div className="env-flap" />
                                <div className="env-seal">M</div>
                            </div>
                        </div>

                        <div className="max-w-md space-y-3">
                            {[
                                "A matter of extreme importance has come to our attention.",
                                "One extraordinary human is celebrating another trip around the sun.",
                                "An independent investigation has confirmed that this person is exceptionally fabulous.",
                            ].map((t, i) => (
                                <span className="mask-line" key={t}>
                                    <motion.span variants={lineUp} custom={d} className="font-display text-lg italic text-[#C4B5FD]/90 sm:text-xl">
                                        {t}
                                    </motion.span>
                                </span>
                            ))}
                        </div>

                        <motion.button
                            data-testid="envelope-seal-button"
                            onClick={reveal}
                            className="btn-gold mt-10"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0, transition: { delay: 1.4 * d, duration: 0.7 } }}
                        >
                            Reveal the identity
                        </motion.button>
                    </motion.div>
                ) : (
                    <motion.div
                        key="revealed"
                        className="relative z-[1] flex flex-col items-center text-center"
                        variants={container}
                        initial="hidden"
                        animate="show"
                    >
                        <div className="envelope-scene mb-12">
                            <div className="envelope" data-open="true">
                                <div className="env-back" />
                                <div className="env-letter-card">
                                    <span className="font-display text-3xl italic text-[#7a5a10]">M.</span>
                                    <span className="font-mono2 text-[9px] tracking-[0.3em] text-[#7a5a10]/70">EST. FABULOUS</span>
                                </div>
                                <div className="env-front" />
                                <div className="env-flap" />
                                <div className="env-seal">M</div>
                            </div>
                        </div>

                        <span className="mask-line">
                            <motion.span variants={lineUp} className="font-display gold-text text-5xl font-semibold italic leading-tight sm:text-7xl">
                                It's you, Mary!
                            </motion.span>
                        </span>
                        <span className="mask-line mt-4">
                            <motion.span variants={lineUp} className="text-base font-light text-[#EDE7F8]/85 sm:text-lg">
                                Welcome to your very own birthday experience.
                            </motion.span>
                        </span>

                        <motion.button
                            data-testid="enter-celebration-button"
                            onClick={exit}
                            className="btn-gold mt-12"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0, transition: { delay: 0.9 * d, duration: 0.7 } }}
                        >
                            Enter my celebration
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
